import { useState, useEffect, useRef, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { motion } from "framer-motion";
import { User, CreditCard, Shield, Calendar, LogOut, Heart, Tv, Crown, Send, Lock, Camera, Plus, X, ChevronRight, MessageCircle, AlertTriangle, FileText } from "lucide-react";
import { SettingsContext } from "@/App";
import { useSiteNavigation, pageLabel } from "@/hooks/useSiteNavigation";

const API = "/api";

function getUser() { try { return JSON.parse(localStorage.getItem('user_data')); } catch { return null; } }

export default function Account() {
  const { settings: siteSettings } = useContext(SettingsContext);
  const { t, i18n } = useTranslation();
  const accountPages = useSiteNavigation("account_menu");
  const membershipEnabled = siteSettings?.membership_enabled !== false;
  const registrationEnabled = siteSettings?.registration_enabled !== false;
  const [user, setUser] = useState(getUser());
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(user ? "dashboard" : "login");
  const [tab, setTab] = useState("overview");
  const [authForm, setAuthForm] = useState({ email: "", password: "", name: "" });
  const [authError, setAuthError] = useState("");
  const [favorites, setFavorites] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [suggestion, setSuggestion] = useState({ channel_name: "", channel_url: "", notes: "" });
  const [suggestionSent, setSuggestionSent] = useState(false);
  const [resetMode, setResetMode] = useState(null);
  const [resetForm, setResetForm] = useState({ email: "", code: "", new_password: "" });
  const [resetMsg, setResetMsg] = useState("");
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);
  const chatBottomRef = useRef(null);
  const navigate = useNavigate();

  const refreshUser = () => {
    axios.get(`${API}/user/me`, { withCredentials: true }).then(res => {
      setUser(res.data);
      localStorage.setItem('user_data', JSON.stringify(res.data));
      setMode("dashboard");
    }).catch(() => {
      localStorage.removeItem('user_data');
      setUser(null);
      setMode("login");
    });
  };

  useEffect(() => {
    if (getUser()) refreshUser();
    axios.get(`${API}/membership/plans`).then(res => setPlans(res.data)).catch(() => {});
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!registrationEnabled && mode === "register") setMode("login");
  }, [registrationEnabled, mode]);

  useEffect(() => {
    if (user && mode === "dashboard") {
      axios.get(`${API}/user/favorites`, { withCredentials: true }).then(res => setFavorites(res.data)).catch(() => {});
    }
  }, [user, mode]);

  useEffect(() => {
    if (user?.has_membership && tab === "messages") {
      const fetchMsgs = () => axios.get(`${API}/chat/messages`, { withCredentials: true }).then(res => setMessages(res.data)).catch(() => {});
      fetchMsgs();
      const poll = setInterval(fetchMsgs, 4000);
      return () => clearInterval(poll);
    }
  }, [user, tab]);

  useEffect(() => { chatBottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError("");
    const endpoint = mode === "login" ? "/user/login" : "/user/register";
    const payload = mode === "login" ? { email: authForm.email, password: authForm.password } : authForm;
    try {
      const res = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      localStorage.setItem('user_data', JSON.stringify(res.data.user));
      setUser(res.data.user);
      setMode("dashboard");
      refreshUser();
    } catch (err) {
      setAuthError(err.response?.data?.detail || "Authentication failed");
    }
  };

  const handleLogout = () => {
    axios.post(`${API}/user/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem('user_data');
    setUser(null);
    setMode("login");
  };

  const handleCheckout = (planId) => {
    // Redirect to membership page for PayPal checkout
    navigate('/membership');
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You will lose access immediately.")) return;
    await axios.post(`${API}/user/cancel-subscription`, {}, { withCredentials: true });
    refreshUser();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/user/upload-avatar`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      refreshUser();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    }
    setAvatarUploading(false);
  };

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    await axios.post(`${API}/chat/send`, { message: newMsg }, { withCredentials: true });
    setNewMsg("");
    const res = await axios.get(`${API}/chat/messages`, { withCredentials: true });
    setMessages(res.data);
  };

  const handleSuggestion = async (e) => {
    e.preventDefault();
    if (!suggestion.channel_name || !suggestion.channel_url) return;
    await axios.post(`${API}/user/suggest-channel`, suggestion, { withCredentials: true });
    setSuggestion({ channel_name: "", channel_url: "", notes: "" });
    setSuggestionSent(true);
    setTimeout(() => setSuggestionSent(false), 3000);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMsg("");
    if (resetMode === "request") {
      await axios.post(`${API}/user/forgot-password`, { email: resetForm.email });
      setResetMsg("If the email exists, a reset code has been sent.");
      setResetMode("confirm");
    } else {
      try {
        await axios.post(`${API}/user/reset-password`, resetForm);
        setResetMsg("Password reset! You can now login.");
        setResetMode(null);
        setMode("login");
      } catch (err) {
        setResetMsg(err.response?.data?.detail || "Reset failed");
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg("");
    try {
      await axios.post(`${API}/user/change-password`, pwForm, { withCredentials: true });
      setPwMsg("Password changed successfully!");
      setPwForm({ current_password: "", new_password: "" });
    } catch (err) {
      setPwMsg(err.response?.data?.detail || "Failed to change password");
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>;

  // === FORGOT PASSWORD ===
  if (resetMode) return (
    <div className="max-w-md mx-auto py-12 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#0F172A] rounded-2xl border border-white/[0.06] p-6">
        <h2 className="text-xl font-bold text-white mb-4"><Lock size={20} className="inline mr-2" />{t("account.resetPassword")}</h2>
        {resetMsg && <p className="text-sm text-[#FFC928] mb-3 bg-[#FFC928]/10 rounded-lg p-2">{resetMsg}</p>}
        <form onSubmit={handleForgotPassword} className="space-y-3">
          <input placeholder={t("account.email")} value={resetForm.email} onChange={e => setResetForm({ ...resetForm, email: e.target.value })} className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" data-testid="reset-email" />
          {resetMode === "confirm" && (
            <>
              <input placeholder={t("account.resetCode")} value={resetForm.code} onChange={e => setResetForm({ ...resetForm, code: e.target.value })} className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" data-testid="reset-code" />
              <input type="password" placeholder={t("account.newPassword")} value={resetForm.new_password} onChange={e => setResetForm({ ...resetForm, new_password: e.target.value })} className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" data-testid="reset-new-password" />
            </>
          )}
          <button type="submit" className="w-full py-2.5 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl font-medium hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all text-sm" data-testid="reset-submit">
            {resetMode === "request" ? t("account.sendResetCode") : t("account.resetPassword")}
          </button>
        </form>
        <button onClick={() => { setResetMode(null); setMode("login"); }} className="mt-3 text-sm text-[#94A3B8] hover:text-white">{t("account.backToLogin")}</button>
      </motion.div>
    </div>
  );

  // === AUTH FORMS ===
  if (mode === "login" || mode === "register") return (
    <div className="max-w-md mx-auto py-12 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#0F172A] rounded-2xl border border-white/[0.06] p-6">
        <h2 className="text-xl font-bold text-white mb-4" data-testid="auth-title">
          <User size={20} className="inline mr-2" />{mode === "login" ? t("account.signInTitle") : t("account.createAccountTitle")}
        </h2>
        {authError && <p className="text-sm text-red-400 mb-3 bg-red-500/10 rounded-lg p-2" data-testid="auth-error">{authError}</p>}
        <form onSubmit={handleAuth} className="space-y-3">
          {mode === "register" && (
            <input data-testid="auth-name" placeholder={t("account.fullName")} value={authForm.name} onChange={e => setAuthForm({ ...authForm, name: e.target.value })}
              className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" />
          )}
          <input data-testid="auth-email" type="email" placeholder={t("account.email")} value={authForm.email} onChange={e => setAuthForm({ ...authForm, email: e.target.value })}
            className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" />
          <input data-testid="auth-password" type="password" placeholder={t("account.password")} value={authForm.password} onChange={e => setAuthForm({ ...authForm, password: e.target.value })}
            className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" />
          <button type="submit" className="w-full py-2.5 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl font-medium hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all text-sm" data-testid="auth-submit">
            {mode === "login" ? t("account.signInTitle") : t("account.createAccountTitle")}
          </button>
        </form>
        <div className="flex items-center justify-between mt-4 text-sm">
          {registrationEnabled ? (
            <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setAuthError(""); }} className="text-[#FFC928] hover:text-[#FFD85A]" data-testid="auth-toggle">
              {mode === "login" ? t("account.createAnAccount") : t("account.alreadyHaveAccount")}
            </button>
          ) : (
            <span className="text-xs text-[#94A3B8]" data-testid="registration-disabled-notice">
              {(i18n.language || "en").startsWith("fr")
                ? "La création de nouveaux comptes est actuellement désactivée."
                : "New account registration is currently disabled."}
            </span>
          )}
          {mode === "login" && (
            <button onClick={() => setResetMode("request")} className="text-[#94A3B8] hover:text-white" data-testid="forgot-password-btn">{t("account.forgotPassword")}</button>
          )}
        </div>
      </motion.div>
    </div>
  );

  // === DASHBOARD ===
  const isPremium = user?.has_membership;
  const tabs = [
    { id: "overview", label: t("account.overview"), icon: User },
    ...(membershipEnabled ? [{ id: "plans", label: t("account.plans"), icon: CreditCard }] : []),
    { id: "favorites", label: t("nav.favorites"), icon: Heart },
    ...(isPremium ? [{ id: "messages", label: t("account.messages"), icon: MessageCircle }] : []),
    { id: "suggest", label: t("account.suggest"), icon: Plus },
    { id: "settings", label: t("nav.settings"), icon: Lock },
  ];

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl" data-testid="user-dashboard">
      {/* Profile Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#0F172A] rounded-2xl border border-white/[0.06] p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="relative group">
            <div className="w-16 h-16 rounded-full bg-[#172033] border-2 border-white/[0.08] flex items-center justify-center overflow-hidden">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={28} className="text-[#94A3B8]" />
              )}
            </div>
            <button onClick={() => fileInputRef.current?.click()} className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-testid="upload-avatar-btn">
              <Camera size={16} className="text-white" />
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
            {avatarUploading && <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full"><div className="w-5 h-5 border-2 border-[#FFC928] border-t-transparent rounded-full animate-spin"></div></div>}
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white" data-testid="user-name">{user?.name || user?.email?.split('@')[0]}</h1>
            <p className="text-sm text-[#94A3B8]">{user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            {membershipEnabled && isPremium ? (
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFC928]/10 text-[#FFC928] rounded-lg text-sm font-medium border border-[#FFC928]/20" data-testid="premium-badge">
                <Crown size={14} /> {t("account.premium")}
              </span>
            ) : membershipEnabled ? (
              <Link to="#" onClick={() => setTab("plans")} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFC928]/10 text-[#FFC928] rounded-lg text-sm font-medium border border-[#FFC928]/20 hover:bg-[#FFC928] hover:text-black transition-colors">
                <CreditCard size={14} /> {t("account.upgrade")}
              </Link>
            ) : null}
            <button onClick={handleLogout} className="p-2 text-[#94A3B8] hover:text-white hover:bg-white/[0.05] rounded-lg transition-colors" data-testid="logout-btn"><LogOut size={18} /></button>
          </div>
        </div>
        {membershipEnabled && isPremium && user?.membership_expires && (
          <div className="mt-3 text-xs text-[#94A3B8] flex items-center gap-1.5">
            <Calendar size={12} /> {t("account.expires")}: {new Date(user.membership_expires).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            {user?.plan_name && <span className="text-[#94A3B8]/60">({user.plan_name})</span>}
          </div>
        )}
      </motion.div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1" data-testid="dashboard-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'bg-[#FFC928]/10 text-[#FFC928] border border-[#FFC928]/20' : 'text-[#94A3B8] hover:text-white hover:bg-white/[0.05]'}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
        {accountPages.map(p => (
          p.new_tab ? (
            <a key={p.slug} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`account-page-${p.slug}`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors text-[#94A3B8] hover:text-white hover:bg-white/[0.05]">
              <FileText size={14} />{pageLabel(p, i18n)}
            </a>
          ) : (
            <Link key={p.slug} to={p.url} data-testid={`account-page-${p.slug}`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors text-[#94A3B8] hover:text-white hover:bg-white/[0.05]">
              <FileText size={14} />{pageLabel(p, i18n)}
            </Link>
          )
        ))}
      </div>

      {/* Tab Content */}
      <motion.div key={tab} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {/* OVERVIEW */}
        {tab === "overview" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {membershipEnabled && (
                <div className="bg-[#0F172A] rounded-xl border border-white/[0.06] p-5">
                  <Shield size={20} className="text-[#FFC928] mb-2" />
                  <div className="text-2xl font-bold text-white">{isPremium ? "Active" : "Free"}</div>
                  <div className="text-xs text-[#94A3B8]">Membership Status</div>
                </div>
              )}
              <div className="bg-[#0F172A] rounded-xl border border-white/[0.06] p-5">
                <Heart size={20} className="text-red-400 mb-2" />
                <div className="text-2xl font-bold text-white">{favorites.length}</div>
                <div className="text-xs text-[#94A3B8]">{t("account.savedChannels")}</div>
              </div>
              <div className="bg-[#0F172A] rounded-xl border border-white/[0.06] p-5">
                <Calendar size={20} className="text-green-400 mb-2" />
                <div className="text-2xl font-bold text-white">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</div>
                <div className="text-xs text-[#94A3B8]">{t("account.memberSince")}</div>
              </div>
            </div>
            {!isPremium && membershipEnabled && (
              <div className="bg-gradient-to-r from-[#FFC928]/10 via-[#FFC928]/5 to-transparent rounded-xl border border-[#FFC928]/20 p-6">
                <h3 className="font-bold text-white mb-1">{t("account.upgradePremium")}</h3>
                <p className="text-sm text-[#94A3B8] mb-4">{t("account.upgradePremiumSubtitle")}</p>
                <button onClick={() => setTab("plans")} className="px-5 py-2 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-lg font-medium hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all text-sm">{t("account.viewPlans")}</button>
              </div>
            )}
            {favorites.length > 0 && (
              <div className="bg-[#0F172A] rounded-xl border border-white/[0.06] p-5">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Heart size={16} className="text-red-400" /> Favorites</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {favorites.slice(0, 6).map(ch => (
                    <Link key={ch.id} to={`/watch/${ch.id}`} className="flex items-center gap-2 p-2.5 bg-[#050814] rounded-lg border border-white/[0.06] hover:border-[#FFC928]/30 transition-colors group">
                      {ch.logo_url ? <img src={ch.logo_url} alt="" className="w-6 h-6 rounded object-cover" onError={e => { e.target.style.display = 'none'; }} /> : <Tv size={14} className="text-[#94A3B8]" />}
                      <span className="text-xs text-[#94A3B8] group-hover:text-white truncate">{ch.name}</span>
                    </Link>
                  ))}
                </div>
                {favorites.length > 6 && <button onClick={() => setTab("favorites")} className="mt-2 text-xs text-blue-400 hover:text-blue-300">View all {favorites.length} favorites</button>}
              </div>
            )}
          </div>
        )}

        {/* PLANS */}
        {tab === "plans" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {plans.map((plan, i) => (
                <div key={plan.id} className={`relative bg-[#0F172A] rounded-xl border p-5 ${i === 1 ? 'border-[#FFC928]/50 ring-1 ring-[#FFC928]/20' : 'border-white/[0.06]'}`}>
                  {i === 1 && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#FFC928] text-black text-[10px] font-bold rounded-full uppercase tracking-wider">Best Value</div>}
                  <h3 className="font-bold text-white text-lg">{plan.name}</h3>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-white">${plan.price}</span>
                    {plan.original_price > 0 && <span className="text-sm text-[#94A3B8] line-through">${plan.original_price.toFixed(2)}</span>}
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-1">{plan.duration_days} days access</p>
                  {plan.original_price > 0 && (
                    <div className="mt-2 inline-block px-2 py-0.5 bg-green-500/10 text-green-400 rounded text-[10px] font-bold">
                      SAVE {Math.round((1 - plan.price / plan.original_price) * 100)}%
                    </div>
                  )}
                  <button
                    onClick={() => handleCheckout(plan.id)}
                    data-testid={`checkout-${plan.id}`}
                    className="mt-4 w-full py-2.5 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-lg font-medium hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all text-sm"
                  >
                    {isPremium ? "Switch Plan" : "Subscribe Now"}
                  </button>
                </div>
              ))}
            </div>
            {authError && <p className="text-sm text-red-400 bg-red-500/10 rounded-lg p-3">{authError}</p>}
            {isPremium && (
              <div className="bg-[#0F172A] rounded-xl border border-white/[0.06] p-5">
                <h3 className="font-bold text-white mb-2 flex items-center gap-2"><AlertTriangle size={16} className="text-amber-400" /> {t("account.cancelSubscription")}</h3>
                <p className="text-sm text-[#94A3B8] mb-3">{t("account.cancelSubtitle")}</p>
                <button onClick={handleCancel} className="px-4 py-2 bg-red-600/10 text-red-400 border border-red-600/20 rounded-lg text-sm hover:bg-red-600 hover:text-white transition-colors" data-testid="cancel-subscription-btn">Cancel Subscription</button>
              </div>
            )}
          </div>
        )}

        {/* FAVORITES */}
        {tab === "favorites" && (
          <div className="bg-[#0F172A] rounded-xl border border-white/[0.06] p-5">
            <h3 className="font-bold text-white mb-4">{t("account.favoritesSaved", { count: favorites.length })}</h3>
            {favorites.length === 0 ? (
              <div className="text-center py-10 text-[#94A3B8] text-sm">
                <Heart size={32} className="mx-auto mb-2 text-[#94A3B8]/40" />
                {t("account.noFavorites")}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {favorites.map(ch => (
                  <Link key={ch.id} to={`/watch/${ch.id}`} className="flex items-center gap-3 p-3 bg-[#050814] rounded-xl border border-white/[0.06] hover:border-[#FFC928]/30 transition-colors group">
                    {ch.logo_url ? <img src={ch.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" onError={e => { e.target.style.display = 'none'; }} /> : <Tv size={20} className="text-[#94A3B8]" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white font-medium truncate group-hover:text-[#FFC928]">{ch.name}</div>
                      <div className="text-xs text-[#94A3B8]">{ch.category_name || 'General'}</div>
                    </div>
                    <ChevronRight size={14} className="text-[#94A3B8]/40" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MESSAGES (Premium only) */}
        {tab === "messages" && isPremium && (
          <div className="bg-[#0F172A] rounded-xl border border-white/[0.06] overflow-hidden" style={{ height: '28rem' }}>
            <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.06] bg-[#050814]/50">
              <MessageCircle size={16} className="text-[#FFC928]" />
              <span className="font-medium text-white text-sm">{t("account.directMessages")}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{ height: 'calc(100% - 7rem)' }}>
              {messages.length === 0 && <div className="text-center text-[#94A3B8] text-xs py-10">{t("account.sendMessage")}</div>}
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm ${m.sender === 'user' ? 'bg-[#FFC928] text-black' : 'bg-[#172033] text-white border border-white/[0.06]'}`}>
                    {m.message}
                    <div className={`text-[10px] mt-0.5 ${m.sender === 'user' ? 'text-black/50' : 'text-[#94A3B8]'}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={chatBottomRef} />
            </div>
            <div className="p-3 border-t border-white/[0.06]">
              <div className="flex gap-2">
                <input data-testid="dm-input" value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  placeholder={t("account.typeMessage")} className="flex-1 px-3 py-2 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm placeholder-[#94A3B8]/60 focus:outline-none" />
                <button onClick={sendMessage} data-testid="dm-send" className="px-4 py-2 bg-[#FFC928] text-black rounded-xl hover:bg-[#FFD85A] transition-colors"><Send size={14} /></button>
              </div>
            </div>
          </div>
        )}

        {/* SUGGEST CHANNEL */}
        {tab === "suggest" && (
          <div className="bg-[#0F172A] rounded-xl border border-white/[0.06] p-5 max-w-lg">
            <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Plus size={16} className="text-[#FFC928]" /> {t("account.suggestChannel")}</h3>
            {suggestionSent && <p className="text-sm text-green-400 mb-3 bg-green-500/10 rounded-lg p-2">{t("account.suggestionSent")}</p>}
            <form onSubmit={handleSuggestion} className="space-y-3">
              <input data-testid="suggest-name" placeholder={t("account.channelName")} value={suggestion.channel_name} onChange={e => setSuggestion({ ...suggestion, channel_name: e.target.value })}
                className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" required />
              <input data-testid="suggest-url" placeholder={t("account.channelUrl")} value={suggestion.channel_url} onChange={e => setSuggestion({ ...suggestion, channel_url: e.target.value })}
                className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" required />
              <textarea data-testid="suggest-notes" placeholder={t("account.additionalNotes")} value={suggestion.notes} onChange={e => setSuggestion({ ...suggestion, notes: e.target.value })} rows={3}
                className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" />
              <button type="submit" className="w-full py-2.5 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl font-medium hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all text-sm" data-testid="suggest-submit">{t("account.submitSuggestion")}</button>
            </form>
          </div>
        )}

        {/* SETTINGS */}
        {tab === "settings" && (
          <div className="space-y-4 max-w-lg">
            <div className="bg-[#0F172A] rounded-xl border border-white/[0.06] p-5">
              <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Lock size={16} className="text-[#94A3B8]" /> {t("account.changePassword")}</h3>
              {pwMsg && <p className={`text-sm mb-3 rounded-lg p-2 ${pwMsg.includes('success') ? 'text-green-400 bg-green-500/10' : 'text-red-400 bg-red-500/10'}`}>{pwMsg}</p>}
              <form onSubmit={handleChangePassword} className="space-y-3">
                <input data-testid="current-password" type="password" placeholder={t("account.currentPassword")} value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" required />
                <input data-testid="new-password" type="password" placeholder={t("account.newPassword")} value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20" required />
                <button type="submit" className="w-full py-2.5 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl font-medium hover:shadow-[0_0_15px_rgba(255,201,40,0.3)] transition-all text-sm" data-testid="change-password-btn">{t("account.updatePassword")}</button>
              </form>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
